import {DialogContent, Stack, Typography} from '@mui/material';
import {BaseDialog} from '@resurs-internal/mui';
import {useCountry, useFeedback} from '@resurs-internal/rb-app-api';
import {FeedbackRequest} from '@resurs-internal/rb-app-api/hooks/useFeedback/useFeedback';
import {useAlertDialog} from 'components';
import {config} from 'config';
import {useTranslate} from 'hooks';
import {useFeedbackDialog} from 'hooks/useDialog/useFeedbackDialog';
import {SubmitHandler} from 'react-hook-form';
import {FeedbackRatingField} from './FeedbackRatingField';
import {FeedbackTextField} from './FeedbackTextField';
import {FeedbackForm, useFeedbackForm} from './useFeedbackForm';

export const FeedbackDialog: React.FC = () => {
  const translate = useTranslate();
  const country = useCountry();
  const dialog = useFeedbackDialog();
  const alerts = useAlertDialog();
  const feedback = useFeedback();
  const form = useFeedbackForm();

  const onSubmit: SubmitHandler<FeedbackForm> = async (data) => {
    try {
      const request = {
        pageId: dialog.state?.pageId,
        rating: data.rating,
        text: data.text,
        country: country.code,
        platform: 'WEB',
        feature: dialog.state?.feature,
        clientVersion: config.clientVersion,
      } as FeedbackRequest;

      await feedback.create.mutateAsync(request);
      dialog.onClose();
      alerts.success({
        title: translate('common.success'),
        subtitle: translate('feedback.success.text'),
      });
    } catch (error) {
      dialog.onClose();
      alerts.error({
        title: translate('common.error'),
        subtitle: JSON.stringify(`${error}`),
      });
    }
  };

  const hasSelectedRating = form.watch('rating') > 0;
  const formIsValid = form.formState.isValid;
  const isSubmitting = form.formState.isSubmitting;

  return (
    <BaseDialog
      id="feedback_modal"
      open={dialog.open}
      onClose={dialog.onClose}
      maxWidth={'sm'}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: dialog.onClose,
        },
        {
          title: translate('feedback.button.text'),
          color: 'primary',
          disabled: !hasSelectedRating || !formIsValid || isSubmitting,
          onClick: form.handleSubmit(onSubmit),
        },
      ]}
      aria-labelledby="feedback-radio-title">
      <DialogContent>
        <Stack spacing={3}>
          <Typography id="feedback-radio-title" textAlign="center" variant="h2">
            {translate('feedback.title')}
          </Typography>
          <FeedbackRatingField name="rating" control={form.control} />
          <FeedbackTextField name="text" control={form.control} />
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
